import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { Button } from "../Button";
import localization from "../../../config/localization";
import { useLanguage } from "../../../config/localization/LanguageProvider";

interface DatePickerProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  isRequired?: boolean;
  variant?: "default" | "auth";
  disabled?: boolean;
  placeholder?: string;
  disableFutureDates?: boolean;
  allowedTextInput?: boolean;
  alwaysOpenDown?: boolean;
}

export function DatePicker({
  label,
  value,
  onChange,
  error,
  isRequired,
  variant = "default",
  disabled = false,
  placeholder,
  disableFutureDates = false,
  allowedTextInput = true,
  alwaysOpenDown = false,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [openUpward, setOpenUpward] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);
  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null,
  );

  const { language } = useLanguage();

  useEffect(() => {
    if (!value) {
      setInputValue("");
      setSelectedDate(null);
      setCurrentMonth(new Date());
      return;
    }

    const parsedDate = new Date(value);

    if (!isNaN(parsedDate.getTime())) {
      setInputValue(format(parsedDate, "dd-MM-yyyy"));
      setSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
    }
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      const clickedOutsideCalendar =
        modalRef.current && !modalRef.current.contains(target);

      const clickedOutsideMonthDropdown =
        showMonthDropdown &&
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(target);

      const clickedOutsideYearDropdown =
        showYearDropdown &&
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(target);

      if (clickedOutsideCalendar) {
        setIsOpen(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
        return;
      }

      if (clickedOutsideMonthDropdown) {
        setShowMonthDropdown(false);
      }

      if (clickedOutsideYearDropdown) {
        setShowYearDropdown(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, showMonthDropdown, showYearDropdown]);

  useEffect(() => {
    if (showYearDropdown && yearDropdownRef.current) {
      const currentYear = currentMonth.getFullYear();
      const selectedYearButton = yearDropdownRef.current.querySelector(
        `button[data-year="${currentYear}"]`,
      );
      if (selectedYearButton) {
        selectedYearButton.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
  }, [showYearDropdown, currentMonth]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;
    const digitsOnly = input.replace(/\D/g, "");
    const limitedDigits = digitsOnly.substring(0, 8);

    let formatted = "";
    if (limitedDigits.length > 0) {
      formatted = limitedDigits.substring(0, 2);
      if (limitedDigits.length >= 3) {
        formatted += "-" + limitedDigits.substring(2, 4);
      }
      if (limitedDigits.length >= 5) {
        formatted += "-" + limitedDigits.substring(4, 8);
      }
    }

    setInputValue(formatted);

    const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = formatted.match(dateRegex);

    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      if (
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31 &&
        year >= 1950 &&
        year <= 2100
      ) {
        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day
        ) {
          onChange(format(date, "dd-MM-yyyy hh:mm:ss"));
          setCurrentMonth(date);
        }
      }
    }
  };

  const handleInputClick = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const calendarHeight = 400;
      if (alwaysOpenDown === true) {
        setOpenUpward(false);
      } else {
        setOpenUpward(spaceBelow < calendarHeight && spaceAbove > spaceBelow);
      }
    }
    setIsOpen(!isOpen);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1),
    );
  };

  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    setSelectedDate(selectedDate);
  };

  const handleYearSelect = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth()));
    setShowYearDropdown(false);
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex));
    setShowMonthDropdown(false);
  };

  const generateYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear + 10; i >= 1900; i--) {
      years.push(i);
    }
    return years;
  };

  const generateMonthRange = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push(
        new Date(currentMonth.getFullYear(), i, 1).toLocaleString(language, {
          month: "long",
        }),
      );
    }
    return months;
  };

  const formatter = new Intl.DateTimeFormat(language, { weekday: "short" });
  const dayNames = [0, 1, 2, 3, 4, 5, 6].map((day) => {
    const date = new Date(2024, 0, 7 + day);
    return formatter.format(date);
  });

  const isSelectedDate = (day: number | null) => {
    if (!day || !selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isFutureDate = (day: number | null) => {
    if (!day || !disableFutureDates) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString(language, {
    month: "long",
  });

  const yearValue = currentMonth.getFullYear();

  const handleClear = () => {
    setSelectedDate(null);
    onChange("");
    setInputValue("");
    setCurrentMonth(new Date());
    setIsOpen(false);
  };

  const handleSubmit = () => {
    if (selectedDate) {
      onChange(format(selectedDate, "yyyy-MM-dd HH:mm:ss"));
      setIsOpen(false);
    }
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-[#A9A9A9] mb-1 ml-1 font-medium">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative" ref={modalRef}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder ?? "dd/mm/yyyy"}
          onClick={handleInputClick}
          disabled={disabled || !allowedTextInput}
          className={`w-full px-4 py-2 h-12 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#333333] cursor-pointer
            ${variant === "default" ? "bg-[#000000]" : "bg-[#1D1D1D]"}
            border ${error ? "border-red-500" : "border-[#333333]"}
            pr-12
          `}
        />

        <Button
          type="button"
          onClick={handleInputClick}
          disabled={disabled}
          className={`absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white shadow-none ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
        >
          <img
            src={"/icons/ic-calendar.svg"}
            alt="calendar"
            className="w-6 h-6"
          />
        </Button>

        {isOpen && (
          <div
            className={`absolute right-0  bg-white rounded-lg shadow-2xl p-5 z-20 w-80 max-h-[420px] overflow-visible ${openUpward ? " bottom-full mb-0" : "top-full mt-2"
              }`}
          >
            <div className="flex items-center justify-between mb-4 ">
              <Button
                type="button"
                onClick={handlePrevMonth}
                className="w-8 h-8 flex items-center justify-center text-[#9C9C9C] hover:bg-gray-100 rounded-full shadow-none"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <div className="relative">
                <h3 className="text-lg font-semibold text-[#CF7A25] flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                    className="hover:bg-gray-100 px-2 py-1 rounded transition-colors shadow-none"
                  >
                    {monthName}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowYearDropdown(!showYearDropdown)}
                    className="hover:bg-gray-100 px-2 py-1 rounded transition-colors shadow-none"
                  >
                    {yearValue}
                  </Button>
                </h3>

                {showMonthDropdown && (
                  <div
                    ref={monthDropdownRef}
                    className="absolute top-full right-0 mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl z-50 w-30 max-h-48 overflow-y-auto"
                  >
                    {generateMonthRange().map((monthName, index) => (
                      <Button
                        key={index}
                        type="button"
                        data-month={index}
                        onClick={() => handleMonthSelect(index)}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${index === currentMonth.getMonth()
                          ? "bg-[#FD3A84] text-white hover:bg-[#e5356e]"
                          : "text-gray-900"
                          } shadow-none`}
                      >
                        {monthName}
                      </Button>
                    ))}
                  </div>
                )}

                {showYearDropdown && (
                  <div
                    ref={yearDropdownRef}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl z-50 w-32 max-h-48 overflow-y-auto"
                  >
                    {generateYearRange().map((year) => (
                      <Button
                        key={year}
                        type="button"
                        data-year={year}
                        onClick={() => handleYearSelect(year)}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${year === yearValue
                          ? "bg-[#FD3A84] text-white hover:bg-[#e5356e]"
                          : "text-gray-900"
                          } shadow-none`}
                      >
                        {year}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                type="button"
                onClick={handleNextMonth}
                className="w-8 h-8 flex items-center justify-center text-[#9C9C9C] hover:bg-gray-100 rounded-full shadow-none"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-bold text-[#4A4A4A] py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => (
                <div key={index} className="aspect-square">
                  {day ? (
                    <div className="relative w-full h-full">
                      <Button
                        type="button"
                        onClick={() => handleDateSelect(day)}
                        disabled={isFutureDate(day)}
                        className={`w-full h-full flex items-center justify-center rounded-full text-sm font-medium transition-colors
                          ${isFutureDate(day)
                            ? "text-gray-300 cursor-not-allowed"
                            : isSelectedDate(day)
                              ? "bg-[#FD3A84] text-white hover:bg-[#e5356e]"
                              : "text-gray-900 hover:bg-gray-100"
                          }
                          shadow-none
                        `}
                      >
                        {day}
                      </Button>
                      {isToday(day) && !isSelectedDate(day) && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#FD3A84] rounded-full"></div>
                      )}
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-4 gap-2">
              <Button
                type="button"
                onClick={handleClear}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium shadow-none"
              >
                {localization.clear}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!selectedDate}
                className={`px-6 py-2 rounded-lg font-medium  ${selectedDate
                  ? "bg-gray-800 text-white hover:bg-gray-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  } shadow-none`}
              >
                {localization.apply}
              </Button>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-red-500 text-sm mt-1 ml-2">{error}</div>}
    </div>
  );
}
